<script setup lang="ts">
import { ref, watch, nextTick } from "vue";
import { useTranslation } from "i18next-vue";
import type { ChatMessage } from "@/types/chatbot.types";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const props = defineProps<{
  messages: ChatMessage[];
  loading: boolean;
}>();

const { t } = useTranslation();
const scrollRef = ref<HTMLElement>();

watch(
  () => props.messages.length,
  async () => {
    await nextTick();
    if (scrollRef.value) {
      scrollRef.value.scrollTop = scrollRef.value.scrollHeight;
    }
  },
);

watch(
  () => props.loading,
  async (val) => {
    if (val) {
      await nextTick();
      if (scrollRef.value) {
        scrollRef.value.scrollTop = scrollRef.value.scrollHeight;
      }
    }
  },
);
</script>

<template>
  <div ref="scrollRef" class="chatbot-messages">
    <!-- Greeting when no messages -->
    <div v-if="messages.length === 0" class="greeting-message">
      <PhosphorIcon
        name="robot"
        :size="28"
        weight="duotone"
        style="opacity: 0.5"
      />
      <p class="mt-2 mb-0">{{ t("help.chatbot.greeting") }}</p>
    </div>

    <!-- Messages -->
    <div
      v-for="msg in messages"
      :key="msg.id"
      class="message-bubble"
      :class="msg.role === 'user' ? 'message-user' : 'message-assistant'"
    >
      <div
        class="message-content"
        v-if="msg.content !== 'error'"
        v-html="formatMessage(msg.content)"
      />
      <div class="message-content message-error" v-else>
        {{ t("help.chatbot.error") }}
      </div>
    </div>

    <!-- Loading indicator -->
    <div v-if="loading" class="message-bubble message-assistant">
      <div class="typing-indicator">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
function formatMessage(content: string): string {
  return (
    content
      // Bold
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      // Links
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
      )
      // Line breaks
      .replace(/\n/g, "<br>")
  );
}
</script>

<style lang="scss" scoped>
.chatbot-messages {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
}

.greeting-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
  color: var(--bs-body-color);
  opacity: 0.7;
  padding: 24px;
}

.message-bubble {
  max-width: 85%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.message-user {
  align-self: flex-end;
  background-color: #2563eb;
  color: white;
  border-bottom-right-radius: 4px;
}

.message-assistant {
  align-self: flex-start;
  background-color: #ffffff;
  color: black;
  border-bottom-left-radius: 4px;
}

.message-content {
  :deep(a) {
    color: inherit;
    text-decoration: underline;
  }

  :deep(strong) {
    font-weight: 600;
  }
}

.message-user .message-content :deep(a) {
  color: white;
}

.message-error {
  color: var(--bs-danger);
  font-style: italic;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 4px 0;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--bs-body-color);
    opacity: 0.4;
    animation: typing 1.4s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes typing {
  0%,
  60%,
  100% {
    opacity: 0.4;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-4px);
  }
}
</style>
